// src/merch/colouringSheet.js
//
// Printable colouring sheets: one page per printable side, the sticker
// drawn as BIG as the page allows (kids get room to colour — the scan is
// scaled back down), with:
//   • one faint grey trim line (the sticker shape) — whitened on import
//   • 4 solid black corner markers — found automatically in a scan/photo
//     and used to straighten + crop it (see scanImport.js)
//
// `sheetLayout()` is shared with the importer: both sides must agree on
// exactly where the markers sit relative to the sticker.

import { BLEED_MM } from "./products";
import { stickerPdfPath } from "./shapes";
import { SHEET_LOGO_OPS, SHEET_LOGO_W } from "./sheetLogo";

const LETTER = { w: 215.9, h: 279.4 };
const MARGIN = 12;
const HEADER = 24;
const FOOTER = 26;
export const MARKER_MM = 8;      // marker square size on paper
export const MARKER_GAP_MM = 5;  // gap between bleed edge and marker
// Orientation mark: a small square just right of the TOP-LEFT marker, so
// the importer knows which way up the sheet is (the 4 markers are identical).
export const ORIENT_MM = 4;
export const ORIENT_GAP_MM = 3;
// Trim line grey (0 = black, 1 = white). 0.90 ≈ 10% grey: faint on paper
// but still survives most home printers. Lighter (0.95 = 5%) risks the
// line not printing at all.
export const TRIM_GREY = 0.9;

// Long strips (shaft stickers) are split into stacked ROWS on the page so
// kids get a decent colouring height; the importer re-joins them.
const ROW_GAP_MM = 7; // gap between rows on paper

/** Page geometry for a sticker. Units: mm; page y goes DOWN.
 *  The "block" is what the markers surround: `rows` pieces of the bleed
 *  rect, each pieceW long, stacked with gapS (sticker mm) between. */
export function sheetLayout(sticker) {
  const BW = sticker.widthMm + BLEED_MM * 2; // bleed rect (what we import)
  const BH = sticker.heightMm + BLEED_MM * 2;
  const aspect = BW / BH;
  const rows = aspect > 6 ? Math.min(4, Math.max(2, Math.round(Math.sqrt(aspect / 3.2)))) : 1;
  const pieceW = BW / rows;
  const ring = MARKER_GAP_MM + MARKER_MM;
  const blockAspectGuess = pieceW / (rows * BH);
  const landscape = blockAspectGuess >= 1.15;
  const pageW = landscape ? LETTER.h : LETTER.w;
  const pageH = landscape ? LETTER.w : LETTER.h;
  const availW = pageW - MARGIN * 2 - ring * 2;
  const availH = pageH - HEADER - FOOTER - ring * 2 - (rows - 1) * ROW_GAP_MM;
  const s = Math.min(availW / pieceW, availH / (rows * BH), 3); // print scale
  const gapS = rows > 1 ? ROW_GAP_MM / s : 0;
  const blockW = pieceW, blockH = rows * BH + (rows - 1) * gapS;
  const ox = (pageW - blockW * s) / 2;
  const oy = HEADER + (pageH - HEADER - FOOTER - blockH * s) / 2;
  // marker centre offset from the block, in STICKER mm
  const g = (MARKER_GAP_MM + MARKER_MM / 2) / s;
  return { pageW, pageH, BW, BH, s, ox, oy, g, rows, pieceW, gapS, blockW, blockH };
}

/** Where a point of the bleed rect (u, v in sticker mm) sits in the block. */
export function stripToBlock(L, u, v) {
  const i = Math.min(L.rows - 1, Math.max(0, Math.floor(u / L.pieceW)));
  return [u - i * L.pieceW, v + i * (L.BH + L.gapS)];
}

/** Marker centres in sticker-mm, relative to the block's top-left:
 *  TL, TR, BR, BL. */
export function markerPointsMm(L) {
  return [
    [-L.g, -L.g],
    [L.blockW + L.g, -L.g],
    [L.blockW + L.g, L.blockH + L.g],
    [-L.g, L.blockH + L.g],
  ];
}

/** The printed trim line, for the importer to erase: the sticker placed at
 *  the bleed offset inside the (re-joined) bleed rect. */
export function guideShapes(sticker) {
  return [{ sticker, ox: BLEED_MM, oy: BLEED_MM }];
}

/* ------------------------- tiny vector PDF ------------------------- */

const PT = 72 / 25.4;
const f = (v) => (Math.round(v * 100) / 100).toString();
const esc = (s) => String(s).replace(/·/g, "-").replace(/[\\()]/g, (c) => "\\" + c).replace(/[^\x20-\x7e]/g, "");

function pageContent({ product, size, zone, L }) {
  const st = zone.sticker;
  const H = L.pageH;
  // page mm (y down) → PDF pt (y up)
  const X = (mm) => mm * PT;
  const Y = (mm) => (H - mm) * PT;
  // block mm → page mm
  const px = (u) => L.ox + u * L.s;
  const py = (v) => L.oy + v * L.s;
  const out = [];

  // trim line, faint grey — one row per piece, each clipped to its piece
  for (let i = 0; i < L.rows; i++) {
    const rowTop = py(i * (L.BH + L.gapS));
    const rowLeft = px(0);
    const rw = L.pieceW * L.s, rh = L.BH * L.s;
    // trim box of the WHOLE sticker, shifted so piece i lands in this row
    const tx = rowLeft + (BLEED_MM - i * L.pieceW) * L.s;
    const ty = rowTop + BLEED_MM * L.s;
    out.push(
      "q",
      `${f(X(rowLeft - 0.5))} ${f(Y(rowTop + rh + 0.5))} ${f(X(rw + 1))} ${f(X(rh + 1))} re W n`,
      `${TRIM_GREY} G`, "1 w",
      stickerPdfPath(st, X(tx), Y(ty), X(st.widthMm * L.s), X(st.heightMm * L.s)),
      "S", "Q"
    );
    // colouring guides: same faint grey, dashed, clipped to this row
    if (zone.guides?.length) {
      out.push(
        "q",
        `${f(X(rowLeft - 0.5))} ${f(Y(rowTop + rh + 0.5))} ${f(X(rw + 1))} ${f(X(rh + 1))} re W n`,
        // …and to the sticker shape itself, so guides never poke past the edge
        stickerPdfPath(st, X(tx), Y(ty), X(st.widthMm * L.s), X(st.heightMm * L.s)), "W n",
        `${TRIM_GREY} G`, "0.7 w", "1 J"
      );
      for (const g of zone.guides) {
        out.push(g.dot ? "[0.6 2] 0 d" : "[2.4 1.8] 0 d");
        g.pts.forEach(([gx, gy], j) => out.push(`${f(X(tx + gx * L.s))} ${f(Y(ty + gy * L.s))} ${j ? "l" : "m"}`));
        if (g.closed) out.push("h");
        out.push("S");
      }
      out.push("Q");
    }
    if (L.rows > 1) {
      // row number + "continues" hint (outside the block, left margin)
      out.push("BT", "/F2 9 Tf", "0.55 0.55 0.55 rg", `${f(X(rowLeft - 7))} ${f(Y(rowTop + rh / 2 + 1.5))} Td`, `(${i + 1}) Tj`, "ET");
    }
  }
  if (L.rows > 1) {
    const yb = py(L.blockH) + 4;
    out.push("BT", "/F1 7.5 Tf", "0.5 0.5 0.5 rg", `${f(X(px(0)))} ${f(Y(yb + 11))} Td`,
      `(${esc(`This sticker is long, so it's split into ${L.rows} rows - row 1 continues onto row 2, and so on.`)}) Tj`, "ET");
  }

  // markers
  out.push("q", "0 0 0 rg");
  for (const [u, v] of markerPointsMm(L)) {
    const cx = px(u), cy = py(v), m = MARKER_MM;
    out.push(`${f(X(cx - m / 2))} ${f(Y(cy + m / 2))} ${f(X(m))} ${f(X(m))} re`, "f");
  }
  {
    // orientation mark beside the top-left marker
    const [u, v] = markerPointsMm(L)[0];
    const ocx = px(u) + MARKER_MM / 2 + ORIENT_GAP_MM + ORIENT_MM / 2, ocy = py(v), o = ORIENT_MM;
    out.push(`${f(X(ocx - o / 2))} ${f(Y(ocy + o / 2))} ${f(X(o))} ${f(X(o))} re`, "f");
  }
  out.push("Q");

  // header: grey logo, "Colouring Sheet", product + side, name line
  const logoTop = 5.5; // mm from the page top
  const logoW = (SHEET_LOGO_W / PT) * 0.9; // mm (printed ~23 mm wide)
  out.push("q", `0.9 0 0 0.9 ${f(X(MARGIN))} ${f(Y(logoTop))} cm`, SHEET_LOGO_OPS, "Q");
  const tx0 = MARGIN + logoW + 3;
  const sideName = String(zone.label || "").replace(new RegExp(`^${product.name}\\s*[·-]\\s*`, "i"), "");
  out.push(
    "BT", "/F2 12 Tf", "0.25 0.25 0.25 rg", `${f(X(tx0))} ${f(Y(logoTop + 4.6))} Td`, "(Colouring Sheet) Tj", "ET",
    "BT", "/F1 9 Tf", "0.25 0.25 0.25 rg", `${f(X(tx0))} ${f(Y(logoTop + 8.6))} Td`, `(${esc(`${product.name.toUpperCase()}:`)}) Tj`, "ET",
    "BT", "/F1 9 Tf", "0.25 0.25 0.25 rg", `${f(X(tx0))} ${f(Y(logoTop + 12.4))} Td`,
    `(${esc(`${product.sheetName || size.label}: ${sideName.toUpperCase()}`)}) Tj`, "ET",
    "BT", "/F1 10 Tf", "0.25 0.25 0.25 rg", `${f(X(L.pageW - MARGIN - 62))} ${f(Y(logoTop + 9))} Td`, "(Name: ______________) Tj", "ET",
  );

  // footer instructions
  const lines = [
    "1. Colour and draw your design. Going a little past the grey line is great - keep names a little in from the edge.",
    "2. Don't colour on the four black squares - the app uses them to line up your drawing.",
    "3. Photo tips: bright even light (near a window is best), no flash, and keep your shadow off the page.",
    "4. Scan it (iPhone: Notes or Files > Scan Documents) or snap a photo from straight above, then upload it in MerchMod.",
  ];
  lines.forEach((t, i) => {
    out.push("BT", "/F1 8.5 Tf", "0.35 0.35 0.35 rg", `${f(X(MARGIN))} ${f(Y(L.pageH - FOOTER + 7 + i * 4.6))} Td`, `(${esc(t)}) Tj`, "ET");
  });
  return out.join("\n");
}

/** Build the colouring-sheet PDF (one page per zone/side; each zone's own
 *  shape and layout). `sides` = zone objects from getSides. Returns a Blob. */
export function buildColouringSheetPdf({ product, size, sides }) {
  const enc = new TextEncoder();
  const objs = [];
  const add = (s) => { objs.push(s); return objs.length; };

  const catalog = add(null);
  const pagesId = add(null);
  const f1 = add("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>");
  const f2 = add("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>");
  const pageIds = [];
  let pageW = 0, pageH = 0;
  for (const zone of sides) {
    const L = sheetLayout(zone.sticker);
    pageW = L.pageW; pageH = L.pageH;
    const content = pageContent({ product, size, zone, L });
    const cId = add(`<< /Length ${enc.encode(content).length} >>\nstream\n${content}\nendstream`);
    pageIds.push(add(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${f(pageW * PT)} ${f(pageH * PT)}] ` +
      `/Resources << /Font << /F1 ${f1} 0 R /F2 ${f2} 0 R >> >> /Contents ${cId} 0 R >>`
    ));
  }
  objs[catalog - 1] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;
  objs[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((i) => `${i} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

  let out = "%PDF-1.4\n";
  const offsets = [];
  objs.forEach((o, i) => {
    offsets.push(enc.encode(out).length);
    out += `${i + 1} 0 obj\n${o}\nendobj\n`;
  });
  const xref = enc.encode(out).length;
  out += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n`;
  for (const o of offsets) out += `${String(o).padStart(10, "0")} 00000 n \n`;
  out += `trailer\n<< /Size ${objs.length + 1} /Root ${catalog} 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new Blob([out], { type: "application/pdf" });
}
